import React, { Fragment } from 'react';
import { CheckIcon } from 'lucide-react';
import { motion } from 'framer-motion';
interface Step {
  id: number;
  title: string;
}
interface StepIndicatorProps {
  steps: Step[];
  currentStep: number;
  onStepClick: (index: number) => void;
  mode: 'onboarding' | 'offboarding';
  setMode: (mode: 'onboarding' | 'offboarding') => void;
}
export function StepIndicator({
  steps,
  currentStep,
  onStepClick,
  mode,
  setMode
}: StepIndicatorProps) {
  return <div className="flex flex-col h-full" data-id="element-184">
      {/* Onboarding / Offboarding Toggle */}
      <div className="hidden md:flex p-1 bg-gray-100 rounded-lg mb-8 mx-4 mt-6 relative" data-id="element-185">
        <motion.button layoutId="onboarding-toggle" onClick={() => setMode('onboarding')} className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-all z-10 ${mode === 'onboarding' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-186">
          Onboarding
        </motion.button>
        <motion.button layoutId="offboarding-toggle" onClick={() => setMode('offboarding')} className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all z-10 ${mode === 'offboarding' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-187">
          Offboarding
        </motion.button>
      </div>

      {/* Vertical Steps (Desktop) */}
      <div className="hidden md:flex flex-col space-y-1 px-3" data-id="element-188">
        {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isActive = index === currentStep;
        const isClickable = isCompleted;
        return <button key={step.id} onClick={() => isClickable && onStepClick(index)} disabled={!isClickable && !isActive} className={`flex items-center w-full text-left px-3 py-2.5 rounded-lg transition-all relative ${isActive ? 'bg-blue-50' : isClickable ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default opacity-60'}`} data-id="element-189">
              {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-[#2563EB] rounded-r-full" data-id="element-190" />}

              <div className="flex-shrink-0 mr-3" data-id="element-191">
                {isCompleted ? <div className="w-6 h-6 rounded-full bg-[#2E844A] flex items-center justify-center" data-id="element-192">
                    <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-193" />
                  </div> : isActive ? <div className="w-6 h-6 rounded-full bg-[#2563EB] flex items-center justify-center" data-id="element-194">
                    <span className="text-white text-xs font-bold" data-id="element-195">
                      {step.id}
                    </span>
                  </div> : <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-196">
                    <span className="text-gray-500 text-xs font-bold" data-id="element-197">
                      {step.id}
                    </span>
                  </div>}
              </div>
              <span className={`text-sm font-medium ${isActive ? 'text-[#2563EB]' : isCompleted ? 'text-gray-700' : 'text-gray-500'}`} data-id="element-198">
                {step.title}
              </span>
            </button>;
      })}
      </div>

      {/* Horizontal Steps (Mobile) */}
      <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white" data-id="element-199">
        {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isActive = index === currentStep;
        const isClickable = isCompleted;
        return <Fragment key={step.id} data-id="element-200">
              <button onClick={() => isClickable && onStepClick(index)} disabled={!isClickable && !isActive} className="flex flex-col items-center group relative" data-id="element-201">
                <div className="flex-shrink-0 mb-1" data-id="element-202">
                  {isCompleted ? <div className="w-6 h-6 rounded-full bg-[#2E844A] flex items-center justify-center" data-id="element-203">
                      <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-204" />
                    </div> : isActive ? <div className="w-6 h-6 rounded-full bg-[#2563EB] flex items-center justify-center ring-4 ring-blue-50" data-id="element-205">
                      <span className="text-white text-xs font-bold" data-id="element-206">
                        {step.id}
                      </span>
                    </div> : <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-207">
                      <span className="text-gray-400 text-xs font-bold" data-id="element-208">
                        {step.id}
                      </span>
                    </div>}
                </div>
                <span className={`text-[10px] font-medium absolute -bottom-4 whitespace-nowrap ${isActive ? 'text-[#2563EB]' : 'text-gray-400'}`} data-id="element-209">
                  {isActive ? step.title : ''}
                </span>
              </button>
              {index < steps.length - 1 && <div className={`flex-1 h-[2px] mx-2 rounded-full ${index < currentStep ? 'bg-[#2E844A]' : 'bg-gray-100'}`} data-id="element-210" />}
            </Fragment>;
      })}
      </div>
    </div>;
}